import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/api/client";
import { Wand2, Search, Loader2, Sparkles, ArrowRight, ShoppingBag } from "lucide-react";

/**
 * Unique-feature widget: user types just a product name; we auto-pick their
 * first Amazon account and generate a live 5-product comparison article with
 * real Amazon prices, titles, and images via the existing PA-API endpoint.
 */
export default function QuickCompare() {
  const [query, setQuery] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/network-sync/amazon-accounts");
        setAccounts(data);
      } catch { /* ignore */ }
    })();
  }, []);

  const configuredAmazon = accounts.find((a) => a.paapi_configured);

  const submit = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setError("");
    if (!configuredAmazon) {
      setError("Add PA-API keys to any Amazon account under PA-API Ops first.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/network-sync/paapi/create-article", {
        account_id: configuredAmazon.id,
        keywords: query,
        asins: [],
      }, { timeout: 90000 });
      setResult(data);
    } catch (err) {
      const status = err.response?.status;
      const raw = err.response?.data;
      const detail = raw?.detail;
      const looksLikeHtml =
        typeof raw === "string" && raw.trim().startsWith("<");
      let msg;
      // 1. Structured backend errors (dict detail with code + message)
      if (detail && typeof detail === "object" && detail.message) {
        msg = detail.message;
      }
      // 2. Any raw HTML response (Cloudflare 502/504 edge page) — never render
      else if (looksLikeHtml || status === 502 || status === 504 || err.code === "ECONNABORTED") {
        msg =
          "The request took too long or the Amazon PA-API is unreachable. This usually means your PA-API keys are missing or invalid — please check them under PA-API Ops and try again.";
      }
      // 3. Auth / bad-request fallbacks
      else if (status === 400 || status === 401 || status === 403) {
        msg =
          formatApiErrorDetail(detail) ||
          (typeof detail === "string" ? detail : "") ||
          "Amazon PA-API rejected the request. Please verify your keys under PA-API Ops.";
      }
      // 4. Everything else
      else {
        msg =
          formatApiErrorDetail(detail) || err.message || "Something went wrong. Please try again.";
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="quick-compare" className="border border-sky-500/25 rounded-xl p-6 bg-gradient-to-br from-sky-500/8 via-slate-900/60 to-transparent">
      <div className="flex items-center gap-3 mb-4">
        <Wand2 className="w-5 h-5 text-sky-400" />
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400">— Unique · Quick Compare</p>
          <h3 className="font-serif text-2xl text-sky-100 mt-1 leading-tight">
            Type a product. Get a <span className="italic text-sky-400">live comparison</span> in 60 seconds.
          </h3>
          <p className="text-xs text-neutral-500 mt-1">
            AI picks 5 similar Amazon products, pulls current prices + images, and writes the article.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input
            data-testid="quick-compare-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. wireless earbuds, kitchen mixer, running shoes…"
            className="w-full bg-slate-950/60 border border-slate-800 rounded-md pl-10 pr-4 py-3 text-sky-50 focus:border-sky-400 outline-none"
          />
        </div>
        <button
          data-testid="btn-quick-compare"
          type="submit"
          disabled={loading || !query.trim()}
          className="py-3 px-6 bg-sky-400 hover:bg-sky-300 text-slate-950 rounded-md font-medium flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {loading ? "Generating (~60s)" : "Generate comparison"}
        </button>
      </form>

      {!configuredAmazon && accounts.length === 0 && (
        <p className="mt-3 text-xs text-sky-400/80 font-mono">
          Connect an Amazon account first under{" "}
          <Link to="/app/affiliate" className="underline hover:text-sky-200">Affiliate Studio → Connect</Link>.
        </p>
      )}
      {!configuredAmazon && accounts.length > 0 && (
        <p className="mt-3 text-xs text-sky-400/80 font-mono">
          Add PA-API keys under{" "}
          <Link to="/app/paapi" className="underline hover:text-sky-200">PA-API Ops</Link>{" "}to enable live compares.
        </p>
      )}

      {error && (
        <div className="mt-4 border border-red-500/40 bg-red-500/5 text-red-300 px-4 py-2.5 text-sm rounded">
          {error}
        </div>
      )}

      {result && (
        <div data-testid="quick-compare-result" className="mt-4 border border-emerald-500/30 bg-emerald-500/5 rounded p-4">
          <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-400 mb-2">
            <ShoppingBag className="w-3 h-3 inline mr-1.5" />
            Article created · {result.products_included} live products
          </p>
          <h4 className="font-serif text-lg text-sky-100 mb-2">{result.title}</h4>
          <Link
            to="/app/affiliate"
            className="text-xs inline-flex items-center gap-1 text-sky-300 hover:text-sky-100"
          >
            View in Content tab <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      )}
    </div>
  );
}
